import { Box, Stack, Typography, useTheme } from '@mui/joy';
import { PERCENT_FORMATTER, SM_PERCENT_FORMATTER } from '../../lib/format';
import UiTooltip from '../Controls/UiTooltip';
import ChartTooltip from './ChartTooltip';
import { useMediaQuery } from '../../hooks/useMediaQuery';

interface IProps {
	topic: string;
	chartData: {
		[x: string]: string | number;
	}[];
	title: string;
	print?: boolean;
}
const DonorDisaggChart = ({
	topic,
	title,
	chartData,
	print = false
}: IProps) => {
	//split rep/dem data to can align bar between charts.
	const rep: { [x: string]: string | number }[] = chartData?.filter(
		({ partyLabel, value, group }) =>
			partyLabel === 'GOP' && value !== 0 && group // group is '' when not disaggregated. prevents loading of previous chart data
	);
	const dem: { [x: string]: string | number }[] = chartData?.filter(
		({ partyLabel, value, group }) =>
			partyLabel === 'DEM' && value !== 0 && group
	);

	return (
		<>
			<Box
				sx={{
					margin: '0 1rem 1rem 0'
				}}
			>
				<Box>
					{chartData && !chartData.length ? (
						<Typography
							level='annotation'
							sx={{ fontStyle: 'italic', mt: '1rem' }}
						>
							No data to display
						</Typography>
					) : null}

					<Box>
						{dem && dem.length ? (
							<Box
								sx={{
									//height: `${maxInParty.dems * spacer}px`,
									transition: 'all 1s ease',
									mb: '2rem'
								}}
							>
								{dem?.map((item) => (
									<Stack
										key={`${item.group}${item.partyLabel}`}
										direction={'row'}
										sx={{ lineHeight: 1.3 }}
										alignItems='center'
									>
										<BoxPctChart
											item={item}
											topic={topic}
											isFiltered={true}
											title={title}
											print={print}
										/>

										<Typography
											level='label'
											sx={{
												fontFamily: 'Source Sans Pro',
												fontSize: '20px',
												fontWeight: 700,
												color: item.color,
												lineHeight: '33.6px'
											}}
										>
											{+item.value < 0.01 && +item.value > 0
												? SM_PERCENT_FORMATTER(+item.value)
												: PERCENT_FORMATTER(+item.value)}
										</Typography>
										<Typography
											level='label'
											sx={{
												fontFamily: 'Source Sans Pro',
												fontSize: '20px',
												color: item.color,
												lineHeight: '33.6px',
												whiteSpace: 'nowrap'
											}}
										>
											&nbsp;
											{item.group}
										</Typography>
									</Stack>
								))}
							</Box>
						) : (
							<Box sx={{ marginTop: '1rem' }} />
						)}

						{rep && rep.length ? (
							<Box
								sx={{
									mt: '.5rem',
									mb: '2rem',
									//height: `${maxInParty.reps * spacer}px`,
									transition: 'all 1s ease'
								}}
							>
								{rep.map((item) => (
									<Stack
										key={`${item.group}${item.partyLabel}`}
										direction={'row'}
										sx={{ lineHeight: 1.3, alignItems: 'center' }}
									>
										<BoxPctChart
											item={item}
											topic={topic}
											isFiltered={true}
											title={title}
											print={print}
										/>

										<Typography
											level='label'
											sx={{
												fontSize: '20px',
												fontWeight: 700,
												color: item.color,
												lineHeight: '33.6px'
											}}
										>
											{+item.value < 0.01 && +item.value > 0
												? SM_PERCENT_FORMATTER(+item.value)
												: PERCENT_FORMATTER(+item.value)}
										</Typography>
										<Typography
											level='label'
											sx={{
												fontSize: '20px',
												color: item.color,
												lineHeight: '33.6px',
												whiteSpace: 'nowrap'
											}}
										>
											&nbsp;
											{item.group}
										</Typography>
									</Stack>
								))}
							</Box>
						) : null}
					</Box>
				</Box>
			</Box>
		</>
	);
};

export default DonorDisaggChart;

interface BoxPctProps {
	item: { [x: string]: string | number };
	topic: string;
	title: string;
	isFiltered: boolean;
	print?: boolean;
}
const BoxPctChart = ({
	item,
	title,
	isFiltered,
	topic,
	print = false
}: BoxPctProps) => {
	const theme = useTheme();
	const matches = useMediaQuery(theme.breakpoints.up('sm'));

	return (
		<UiTooltip
			key={item.group}
			title={
				<ChartTooltip
					titleText={title}
					d={item as { [x: string]: string | number }}
					topic={topic}
					isFiltered={isFiltered}
					donor={true}
				/>
			}
			variant='plain'
			sx={{ borderRadius: '0px' }}
			arrow
			placement={!matches ? 'top' : 'left'}
		>
			<Box
				aria-label='a colored bar indicating the percentage for a disaggregated group'
				sx={{
					position: 'relative',
					width: '50px',
					height: '12px',
					margin: print ? '1.5rem .5rem 0 2px' : '0 .5rem 0 2px'
				}}
			>
				<Box
					sx={{
						width: '50px',
						height: '12px',
						position: 'absolute',
						top: 0,
						left: 0,
						border: '1px solid #333',
						backgroundColor: 'transparent',
						zIndex: 3
					}}
				></Box>
				<Box
					sx={{
						position: 'absolute',
						top: 0,
						left: 0,
						//border: '1px solid black',
						width: `${(item.value as number) * 100}%`,
						transition: 'all 1s ease',
						height: '12px',
						backgroundColor: item.color,
						zIndex: 1
					}}
				></Box>
			</Box>
		</UiTooltip>
	);
};
